import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { ICONS } from 'components/Icon';
import { IconButton } from 'components/IconButton';
import {
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import {
  NAVIGATION_HEADER_HEIGHT,
  NavigationHeader,
} from 'components/NavigationHeader';
import { PageFlatList } from 'components/Page';
import { FieldRepeatableListItem } from 'screens/AssetScreen/Field/FieldRepeatableListItem';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';
import * as React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL, SET_GENERIC } from 'store/app/actions';
import {
  selectAssetFieldRepeatableInstanceIdsForGroup,
  selectAssetFieldRepeatableInstanceIdsForGroupFilteredAndSorted,
  selectAssetOrganisationId,
} from 'store/assets/selectors';
import { selectOrganisationFieldGroup } from 'store/organisations/selectors';
import {
  doAssetFindFieldInstanceByPin,
  doAssetFindFieldInstanceByQR,
  doAssetOpenOverview,
} from 'store/actions';
import { pluralise } from 'lib/pluralise';
import { GENERICKEY_FIELDREPEATABLELIST } from 'lib/riskbase';
import { selectGeneric } from 'store/app/selectors';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { TextField } from 'components/Field/TextField';
import { animateNext } from 'lib/animateNext';
import { ScreenSize } from 'lib/constants';
import {
  selectUserOrganisationFeatures,
  selectUserOrganisationUuidToUid,
} from 'store/auth/selectors';
import { ListRenderItem } from '@shopify/flash-list';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.ASSETFIELDREPEATABLE],
  Screens.ASSETFIELDREPEATABLE_LIST
>;

export const FieldRepeatableListScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();
  const renderCountRef = React.useRef(0);
  renderCountRef.current += 1;

  const { assetId, groupId } = route.params;

  const organisationId = useSelector((state) =>
    selectAssetOrganisationId(state, assetId),
  );

  const organisationUid = useSelector((state) =>
    selectUserOrganisationUuidToUid(state, organisationId),
  );
  const userFeatures = useSelector((state) =>
    selectUserOrganisationFeatures(state, organisationUid),
  );

  const instanceIds = useSelector((state) =>
    selectAssetFieldRepeatableInstanceIdsForGroup(state, assetId, groupId),
  );

  const instanceIdsFilteredAndSorted = useSelector((state) =>
    selectAssetFieldRepeatableInstanceIdsForGroupFilteredAndSorted(
      state,
      assetId,
      groupId,
    ),
  );

  const group = useSelector((state) =>
    selectOrganisationFieldGroup(state, organisationId, groupId),
  );

  const genericKeySearchString = GENERICKEY_FIELDREPEATABLELIST({
    groupId,
    where: 'onasset',
    type: 'searchString',
  });

  const searchValue =
    (useSelector((state) =>
      selectGeneric(state, genericKeySearchString),
    ) as string) || '';

  const searchValueRef = React.useRef(searchValue);
  const [showSearch, setShowSearch] = React.useState<boolean>(!!searchValue);

  const renderItem: ListRenderItem<string> = React.useCallback(
    ({ item }) => (
      <FieldRepeatableListItem
        key={item}
        id={item}
        group={group!}
        assetId={assetId}
        isEditable={userFeatures.editassetfields}
      />
    ),
    [assetId, group, userFeatures.editassetfields],
  );

  const handleSearch = React.useCallback(
    (search: string) => {
      searchValueRef.current = search;
      dispatch(
        SET_GENERIC({
          key: genericKeySearchString,
          value: search,
          temp: true,
        }),
      );
    },
    [dispatch, genericKeySearchString],
  );

  const handleSearchBlur = React.useCallback(() => {
    if (!searchValueRef.current) {
      animateNext(() => setShowSearch(false));
    }
  }, []);

  React.useEffect(() => {
    if (!group) {
      if (screenSize === ScreenSize.large) {
        dispatch(doAssetOpenOverview({ assetId }));
      } else {
        navigation.goBack();
      }
    }
  }, [group, dispatch, screenSize, assetId, navigation]);

  if (!group) {
    return null;
  }

  const { pinFieldId, qrFieldId } = group.calculatedInfo;

  return (
    <PageFlatList
      isInModal={false}
      header={
        <ViewWithSeparators>
          <NavigationHeader
            heading={pluralise(
              instanceIds.length,
              `1 ${group.calculatedInfo.singularName}`,
              `{x} ${group.name}`,
              group.name,
            )}
            onPressBackButton={
              screenSize === 'small' ? navigation.goBack : undefined
            }
            iconButtonRight={
              userFeatures.editassetfields
                ? {
                    icon: ICONS.PlusLight,
                    onPress: () =>
                      dispatch(
                        NAVIGATE_MODAL({
                          screen: ModalScreens.NEWREPEATABLEINSTANCE,
                          params: {
                            assetId,
                            groupId,
                          },
                        }),
                      ),
                  }
                : undefined
            }
          />
          {!!instanceIds.length && (
            <View
              style={{
                flexDirection: 'row',
                paddingHorizontal: theme.paddingHorizontal,
                minHeight: NAVIGATION_HEADER_HEIGHT,
                alignItems: 'center',
              }}
            >
              <IconButton
                icon={ICONS.SlidersUpLight}
                onPress={() =>
                  dispatch(
                    NAVIGATE_MODAL({
                      screen: ModalScreens.FIELDINSTANCESOPTIONS,
                      params: { groupId, organisationId },
                    }),
                  )
                }
              />
              {!showSearch && (
                <IconButton
                  icon={ICONS.MagnifyingGlassLight}
                  onPress={() => animateNext(() => setShowSearch(true))}
                />
              )}
              <View style={{ flex: 1 }} />
              {!!qrFieldId && (
                <IconButton
                  icon={ICONS.QRCodeLight}
                  style={{ marginVertical: -10 }}
                  onPress={() =>
                    dispatch(doAssetFindFieldInstanceByQR({ assetId, groupId }))
                  }
                />
              )}
              {!!pinFieldId && (
                <IconButton
                  icon={ICONS.LocationPinSolidStroke}
                  style={{ marginVertical: -10 }}
                  onPress={() =>
                    dispatch(
                      doAssetFindFieldInstanceByPin({ assetId, groupId }),
                    )
                  }
                />
              )}
            </View>
          )}
          {!!instanceIds.length && showSearch && (
            <TextField
              clearable
              autoFocus={renderCountRef.current !== 1}
              indentActions
              onChange={handleSearch}
              onBlur={handleSearchBlur}
              value={searchValue}
              placeholder={`Search ${group.name}`}
              iconLeft={ICONS.MagnifyingGlassLight}
              onChangeDebounceMs={300}
              inputStyle={{ minHeight: 48 }}
              containerStyle={{
                minHeight: 48,
                backgroundColor: 'transparent',
                paddingLeft: theme.paddingHorizontal,
              }}
            />
          )}
        </ViewWithSeparators>
      }
      contentPaddingTop={theme.paddingHorizontal}
      contentPaddingBottomAdditional={
        theme.paddingHorizontal + theme.paddingHorizontalHalf
      }
      emptyConfig={{
        text:
          instanceIds.length || !userFeatures.editassetfields
            ? 'No Results Found'
            : '',
        subtext: instanceIds.length ? 'Try a different search' : '',
        button:
          instanceIds.length || !userFeatures.editassetfields
            ? undefined
            : {
                onPress: () =>
                  dispatch(
                    NAVIGATE_MODAL({
                      screen: ModalScreens.NEWREPEATABLEINSTANCE,
                      params: {
                        assetId,
                        groupId,
                      },
                    }),
                  ),
                text: `Add ${group.calculatedInfo.singularName}`,
              },
      }}
      listConfig={{
        keyExtractor: (id) => id,
        data: instanceIdsFilteredAndSorted,
        renderItem,
      }}
    />
  );
};
